"use client";

import { useState } from "react";
import type { TabItem, RowItem, ColumnItem, ValueItem, Form6Config } from "../types";
import InlineEditor from "./InlineEditor";

interface DataTableProps {
  tab: TabItem;
  rows: RowItem[];
  columns: ColumnItem[];
  config: Form6Config;
  getValue: (tabId: number, rowId: number, colId: number) => ValueItem | null;
  onUpdateCategoryHeader: (
    tabId: number,
    headerValue: string,
    textColor: string,
    bgColor: string,
    borderColor: string
  ) => void;
  onUpdateColumnName: (
    colId: number,
    name: string,
    textColor?: string,
    bgColor?: string,
    borderColor?: string
  ) => void;
  onUpdateRowName: (
    rowId: number,
    name: string,
    textColor?: string,
    bgColor?: string,
    borderColor?: string
  ) => void;
  onSaveCell: (
    tabId: number,
    rowId: number,
    colId: number,
    value: string,
    textColor: string,
    bgColor: string,
    borderColor: string
  ) => Promise<boolean>;
}

const DataTable = ({
  tab,
  rows,
  columns,
  config,
  getValue,
  onUpdateCategoryHeader,
  onUpdateColumnName,
  onUpdateRowName,
  onSaveCell,
}: DataTableProps) => {
  // Category header editing
  const [editingCategoryHeader, setEditingCategoryHeader] = useState(false);
  const [categoryHeaderValue, setCategoryHeaderValue] = useState(tab.category_header || config.categoryHeaderLabel || "Category");
  const [categoryHeaderTextColor, setCategoryHeaderTextColor] = useState(tab.category_header_text_color || "");
  const [categoryHeaderBgColor, setCategoryHeaderBgColor] = useState(tab.category_header_bg_color || "");
  const [categoryHeaderBorderColor, setCategoryHeaderBorderColor] = useState(tab.category_header_border_color || "");

  // Column editing
  const [editingColumnId, setEditingColumnId] = useState<number | null>(null);
  const [editingColumnName, setEditingColumnName] = useState("");
  const [editingColumnTextColor, setEditingColumnTextColor] = useState("");
  const [editingColumnBgColor, setEditingColumnBgColor] = useState("");
  const [editingColumnBorderColor, setEditingColumnBorderColor] = useState("");

  // Row editing
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editingRowName, setEditingRowName] = useState("");
  const [editingRowTextColor, setEditingRowTextColor] = useState("");
  const [editingRowBgColor, setEditingRowBgColor] = useState("");
  const [editingRowBorderColor, setEditingRowBorderColor] = useState("");

  // Cell editing
  const [editingCell, setEditingCell] = useState<{ rowId: number; colId: number } | null>(null);
  const [editingValue, setEditingValue] = useState("");
  const [editingTextColor, setEditingTextColor] = useState("");
  const [editingBgColor, setEditingBgColor] = useState("");
  const [editingBorderColor, setEditingBorderColor] = useState("");

  if (rows.length === 0 || columns.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500 text-sm bg-gray-50 rounded">
        Add {config.rowsLabel?.toLowerCase()} and {config.columnsLabel?.toLowerCase()} above to build the table.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-slate-700 text-white">
            {/* Category Header */}
            {config.showCategoryHeader && (
              <th
                className="border px-3 py-2 text-left font-semibold cursor-pointer hover:opacity-80"
                style={{
                  backgroundColor: tab.category_header_bg_color || undefined,
                  color: tab.category_header_text_color || undefined,
                  borderColor: tab.category_header_border_color || "#475569",
                }}
                onClick={() => {
                  if (!editingCategoryHeader) {
                    setCategoryHeaderValue(tab.category_header || config.categoryHeaderLabel || "Category");
                    setCategoryHeaderTextColor(tab.category_header_text_color || "");
                    setCategoryHeaderBgColor(tab.category_header_bg_color || "");
                    setCategoryHeaderBorderColor(tab.category_header_border_color || "");
                    setEditingCategoryHeader(true);
                  }
                }}
              >
                {editingCategoryHeader ? (
                  <InlineEditor
                    name={categoryHeaderValue}
                    textColor={categoryHeaderTextColor}
                    bgColor={categoryHeaderBgColor}
                    borderColor={categoryHeaderBorderColor}
                    onNameChange={setCategoryHeaderValue}
                    onTextColorChange={setCategoryHeaderTextColor}
                    onBgColorChange={setCategoryHeaderBgColor}
                    onBorderColorChange={setCategoryHeaderBorderColor}
                    onSave={() => {
                      onUpdateCategoryHeader(
                        tab.id,
                        categoryHeaderValue,
                        categoryHeaderTextColor,
                        categoryHeaderBgColor,
                        categoryHeaderBorderColor
                      );
                      setEditingCategoryHeader(false);
                    }}
                    onCancel={() => setEditingCategoryHeader(false)}
                    placeholder="Header name"
                    defaultTextColor="#ffffff"
                    defaultBgColor="#334155"
                    defaultBorderColor="#475569"
                    showTextColor={config.showTextColor !== false}
                    showBgColor={config.showBgColor !== false}
                    showBorderColor={config.showBorderColor !== false}
                  />
                ) : (
                  <span className="hover:underline">{tab.category_header || config.categoryHeaderLabel || "Category"}</span>
                )}
              </th>
            )}

            {/* Column Headers */}
            {columns.map((col) => (
              <th
                key={col.id}
                className="border px-3 py-2 text-center font-semibold cursor-pointer hover:opacity-80"
                style={{
                  backgroundColor: col.bg_color || undefined,
                  color: col.text_color || undefined,
                  borderColor: col.border_color || "#475569",
                }}
                onClick={() => {
                  if (editingColumnId !== col.id) {
                    setEditingColumnId(col.id);
                    setEditingColumnName(col.name);
                    setEditingColumnTextColor(col.text_color || "");
                    setEditingColumnBgColor(col.bg_color || "");
                    setEditingColumnBorderColor(col.border_color || "");
                  }
                }}
              >
                {editingColumnId === col.id && config.showColumnStyling ? (
                  <InlineEditor
                    name={editingColumnName}
                    textColor={editingColumnTextColor}
                    bgColor={editingColumnBgColor}
                    borderColor={editingColumnBorderColor}
                    onNameChange={setEditingColumnName}
                    onTextColorChange={setEditingColumnTextColor}
                    onBgColorChange={setEditingColumnBgColor}
                    onBorderColorChange={setEditingColumnBorderColor}
                    onSave={() => {
                      onUpdateColumnName(
                        col.id,
                        editingColumnName,
                        editingColumnTextColor,
                        editingColumnBgColor,
                        editingColumnBorderColor
                      );
                      setEditingColumnId(null);
                    }}
                    onCancel={() => setEditingColumnId(null)}
                    placeholder={`${config.columnsSingularLabel} name`}
                    defaultTextColor="#ffffff"
                    defaultBgColor="#334155"
                    defaultBorderColor="#475569"
                    showTextColor={config.showTextColor !== false}
                    showBgColor={config.showBgColor !== false}
                    showBorderColor={config.showBorderColor !== false}
                  />
                ) : (
                  <span className="hover:underline">{col.name}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="hover:bg-gray-50">
              {/* Row Name */}
              {config.showCategoryHeader && (
                <td
                  className="border px-3 py-2 font-medium cursor-pointer hover:opacity-80"
                  style={{
                    backgroundColor: row.bg_color || "#f9fafb",
                    color: row.text_color || undefined,
                    borderColor: row.border_color || undefined,
                  }}
                  onClick={() => {
                    setEditingRowId(row.id);
                    setEditingRowName(row.name);
                    setEditingRowTextColor(row.text_color || "");
                    setEditingRowBgColor(row.bg_color || "");
                    setEditingRowBorderColor(row.border_color || "");
                  }}
                >
                  {editingRowId === row.id && config.showRowStyling ? (
                    <InlineEditor
                      name={editingRowName}
                      textColor={editingRowTextColor}
                      bgColor={editingRowBgColor}
                      borderColor={editingRowBorderColor}
                      onNameChange={setEditingRowName}
                      onTextColorChange={setEditingRowTextColor}
                      onBgColorChange={setEditingRowBgColor}
                      onBorderColorChange={setEditingRowBorderColor}
                      onSave={() => {
                        onUpdateRowName(
                          row.id,
                          editingRowName,
                          editingRowTextColor,
                          editingRowBgColor,
                          editingRowBorderColor
                        );
                        setEditingRowId(null);
                      }}
                      onCancel={() => setEditingRowId(null)}
                      placeholder={`${config.rowsSingularLabel} name`}
                      defaultTextColor="#000000"
                      defaultBgColor="#f9fafb"
                      defaultBorderColor="#e2e8f0"
                      showTextColor={config.showTextColor !== false}
                      showBgColor={config.showBgColor !== false}
                      showBorderColor={config.showBorderColor !== false}
                    />
                  ) : (
                    <span className="hover:underline">{row.name}</span>
                  )}
                </td>
              )}

              {/* Cell Values */}
              {columns.map((col) => {
                const value = getValue(tab.id, row.id, col.id);
                const isEditing =
                  editingCell?.rowId === row.id && editingCell?.colId === col.id;

                return (
                  <td
                    key={col.id}
                    className="border px-3 py-2 text-center cursor-pointer hover:opacity-80"
                    style={{
                      backgroundColor: value?.bg_color || undefined,
                      color: value?.text_color || undefined,
                      borderColor: value?.border_color || undefined,
                    }}
                    onClick={() => {
                      if (!isEditing) {
                        setEditingCell({ rowId: row.id, colId: col.id });
                        setEditingValue(value?.value || "");
                        setEditingTextColor(value?.text_color || "");
                        setEditingBgColor(value?.bg_color || "");
                        setEditingBorderColor(value?.border_color || "");
                      }
                    }}
                  >
                    {isEditing && config.showCellStyling ? (
                      <InlineEditor
                        name={editingValue}
                        textColor={editingTextColor}
                        bgColor={editingBgColor}
                        borderColor={editingBorderColor}
                        onNameChange={setEditingValue}
                        onTextColorChange={setEditingTextColor}
                        onBgColorChange={setEditingBgColor}
                        onBorderColorChange={setEditingBorderColor}
                        onSave={async () => {
                          const success = await onSaveCell(
                            tab.id,
                            row.id,
                            col.id,
                            editingValue,
                            editingTextColor,
                            editingBgColor,
                            editingBorderColor
                          );
                          if (success) {
                            setEditingCell(null);
                          }
                        }}
                        onCancel={() => setEditingCell(null)}
                        placeholder="Enter value"
                        defaultTextColor="#000000"
                        defaultBgColor="#ffffff"
                        defaultBorderColor="#e2e8f0"
                      />
                    ) : (
                      <span>{value?.value || "-"}</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DataTable;

